import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';
import type { AuditEntry, TenantUser } from '../lib/types';
import { AUDIT_LABEL, dateTime } from '../lib/labels';
import { Alert, Button, Card, EmptyState, PageHeader, Table, errorMessage } from '../components/ui';

export function AuditPage() {
  const { tenant } = useSession();
  const logs = useResource(
    () => api.get<AuditEntry[]>('/v1/audit-logs?limit=200', { tenant: true }),
    [tenant?.id],
  );
  // Resolve nomes dos autores entre os usuários da empresa (ids de fora aparecem abreviados).
  const users = useResource(
    () => api.get<TenantUser[]>('/v1/users', { tenant: true }).catch(() => []),
    [tenant?.id],
  );
  const nameOf = (id: string | null) => {
    if (!id) return 'Sistema';
    return users.data?.find((u) => u.id === id)?.name ?? `${id.slice(0, 8)}…`;
  };

  return (
    <>
      <PageHeader
        title="Auditoria"
        description="Registro imutável das ações nesta empresa. Os 200 eventos mais recentes."
        actions={
          <Button variant="secondary" onClick={() => void logs.reload()} busy={logs.loading}>
            Atualizar
          </Button>
        }
      />
      <Card>
        {logs.error != null && <Alert tone="error">{errorMessage(logs.error)}</Alert>}
        <Table
          head={['Quando', 'Ação', 'Autor', 'Alvo']}
          empty={
            !logs.loading && logs.data?.length === 0 && <EmptyState>Nenhum evento.</EmptyState>
          }
        >
          {logs.data?.map((l) => (
            <tr key={l.id}>
              <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                {dateTime.format(new Date(l.createdAt))}
              </td>
              <td className="px-4 py-2 text-slate-900">
                {AUDIT_LABEL[l.action] ?? <code className="text-xs">{l.action}</code>}
              </td>
              <td className="px-4 py-2 text-slate-700">{nameOf(l.actorId)}</td>
              <td className="px-4 py-2 text-xs text-slate-500">
                {l.targetType ? `${l.targetType} ${l.targetId?.slice(0, 8) ?? ''}` : '—'}
              </td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}
